import { translate } from '#packages/i18n';
import type { WidgetContext } from '@wix/chat-widget-common-widgets';
import { type Button as ButtonData } from '@wix/common-widget-types';
import { Badge, Box, Text } from '@wix/design-system';
import React, { useState, type FC } from 'react';
import { ACTIONS, SHOW_MORE_KEY } from '../../../constants';
import { SINGLE_SELECT_WIDGET } from '../../singleSelectWidget/singleSelectWidget';
import { WidgetButton } from '../WidgetButton/WidgetButton';

export interface ShowMoreConfig {
  maxToShowByDefault: number;
  exceptions: number[];
}

interface WidgetLayoutProps {
  badge?: string;
  text?: string;
  respondValues?: string[];
  secondaryButton?: ButtonData;
  primaryButton?: ButtonData;
  context: WidgetContext;
  showMore?: ShowMoreConfig;
}

export const WidgetLayout: FC<WidgetLayoutProps> = ({
  badge,
  text,
  respondValues = [],
  children,
  secondaryButton,
  primaryButton,
  context,
  showMore,
}) => {
  const { theme, onWidgetAction } = context;
  const withHeader = badge || text;
  const childrenArray = React.Children.toArray(children);
  const [maxToShow, setMaxToShow] = useState(
    showMore.exceptions.includes(childrenArray.length)
      ? childrenArray.length
      : showMore.maxToShowByDefault || childrenArray.length,
  );
  const withShowMore = showMore && childrenArray.length > maxToShow;
  const withFooter = secondaryButton || primaryButton || withShowMore;

  return (
    <Box direction="vertical" width="100%" gap="SP2">
      {withHeader && (
        <Box direction="vertical" gap="SP2" align="left">
          {badge && (
            <Badge
              skin={theme === 'dark' ? 'neutralLight' : 'neutralStandard'}
              size="tiny"
            >
              {badge}
            </Badge>
          )}

          {text && (
            <Text size="tiny" weight="normal" light={theme === 'dark'}>
              {text}
            </Text>
          )}
        </Box>
      )}

      <Box direction="vertical" gap="SP2">
        {childrenArray.slice(0, maxToShow)}
      </Box>

      {withFooter && (
        <Box
          align="space-between"
          verticalAlign="middle"
          flexWrap="wrap-reverse"
          gap="SP1"
        >
          {withShowMore && (
            <WidgetButton
              type="textButton"
              context={context}
              onClick={() => {
                setMaxToShow(childrenArray.length);

                onWidgetAction?.(SINGLE_SELECT_WIDGET, {
                  action: ACTIONS.BUTTON_CLICK,
                  value: SHOW_MORE_KEY,
                });
              }}
            >
              {translate(SHOW_MORE_KEY)}
            </WidgetButton>
          )}

          {secondaryButton?.label && (
            <WidgetButton
              type="textButton"
              context={context}
              webAction={secondaryButton.webAction}
              respondText={secondaryButton.respondText}
              respondValues={respondValues}
              icon={secondaryButton.icon}
            >
              {secondaryButton.label}
            </WidgetButton>
          )}

          {primaryButton?.label && (
            <WidgetButton
              type="button"
              context={context}
              webAction={primaryButton.webAction}
              respondText={primaryButton.respondText}
              respondValues={respondValues}
              icon={primaryButton.icon}
            >
              {primaryButton.label}
            </WidgetButton>
          )}
        </Box>
      )}
    </Box>
  );
};
